import { type ComponentProps, use } from 'react'
import { Toggle as TogglePrimitive } from '@base-ui/react/toggle'
import { AnimatePresence, motion } from 'motion/react'

import { cn } from '@gto/ui/cn'

import { type ToggleButtonSize, toggleStyles } from './toggle-button.styles'
import { ToggleButtonGroupContext } from './toggle-button-group-context'
import { escapeToggleValue } from './toggle-button-value'

export type { ToggleButtonSize }

export type ToggleButtonProps = ComponentProps<typeof TogglePrimitive> & {
  size?: ToggleButtonSize
  value?: string
  disabled?: boolean
  /** Merged onto the sliding/fade indicator span, for consumers that need a non-default pressed look. */
  indicatorClassName?: string
}

export const ToggleButton = ({
  size,
  className,
  indicatorClassName,
  children,
  value,
  disabled: disabledProp,
  ...props
}: ToggleButtonProps) => {
  const context = use(ToggleButtonGroupContext)
  const resolvedSize = size ?? context?.size ?? 'small'
  const resolvedDisabled = disabledProp ?? context?.disabled ?? false
  const { button, indicator } = toggleStyles({
    size: resolvedSize,
    disabled: resolvedDisabled,
  })

  const isInsideGroup = context !== null
  const isPressed = context?.selectedValues.includes(value ?? '') ?? false
  const showSlidingIndicator = isInsideGroup && context.single && isPressed
  const showFadeIndicator = isInsideGroup && !context.single
  // A group sizes its entries: an equal share of the row by default, or each entry's own
  // content width when the group hugs.
  //
  // `grow shrink-0` and not `flex-auto shrink-0`: `cn` is tailwind-merge, and the `flex`
  // shorthand also sets flex-shrink, so it silently drops the `shrink-0` beside it. The two
  // longhands do not collide.
  const groupWidth = context === null ? false : context.hug ? 'w-auto grow shrink-0' : 'w-full'

  return (
    <TogglePrimitive
      {...props}
      className={cn(button(), groupWidth, className)}
      // Read back when a brushing group measures its row: the gesture resolves buttons
      // from geometry, so each one has to be findable and identifiable in the DOM.
      disabled={resolvedDisabled}
      // Escaped for Base UI only — it treats a falsy value as no value. Everything on our own
      // side of the boundary, including `isPressed` above and `data-brush-value` below, keeps
      // the caller's raw value. See `toggle-button-value.ts`.
      value={value === undefined ? undefined : escapeToggleValue(value)}
      data-brush-value={
        value !== undefined && context?.brushValues.includes(value) === true ? value : undefined
      }
    >
      {showSlidingIndicator && (
        <SlidingIndicator
          className={cn(indicator(), indicatorClassName)}
          layoutId={context.layoutId}
        />
      )}
      {showFadeIndicator && (
        <FadeIndicator
          className={cn(indicator(), indicatorClassName)}
          isActive={isPressed}
        />
      )}
      {children}
    </TogglePrimitive>
  )
}

type SlidingIndicatorProps = { className: string; layoutId: string }

const SlidingIndicator = ({ className, layoutId }: SlidingIndicatorProps) => {
  return (
    <motion.span
      className={className}
      layoutId={layoutId}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    />
  )
}

type FadeIndicatorProps = { className: string; isActive: boolean }

const FadeIndicator = ({ className, isActive }: FadeIndicatorProps) => {
  return (
    <AnimatePresence>
      {isActive && (
        <motion.span
          key="fade-indicator"
          animate={{ opacity: 1 }}
          className={className}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        />
      )}
    </AnimatePresence>
  )
}
